import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { toastSuccess, toastError } from "../../utils";
import "./styles/ProjectSheet.scss";
import { calculateDifferenceInDays } from "../../shared/utils/calculateDifferenceInDays";
import AppContext from "../../context/appContext";
import ProjectSheetContext from "../../context/sheetContext";
import UserContext from "../../context/userContext";
import CloudIcon from "../../components/VectorIcons/CloudIcon";
import EditIcon from "../../components/VectorIcons/EditIcon";
import InfoPaperBasic from "./InfoPaper/InfoPaperBasic/InfoPaperBasic";
import InfoPaperComplementary from "./InfoPaper/InfoPaperComplementary/InfoPaperComplementary";
import InfoPaperRelevance from "./InfoPaper/InfoPaperRelevance/InfoPaperRelevance";
import InfoCard from "../../components/InfoCard/InfoCard";
import TableLoading from "../../components/TableLoading/TableLoading";
import RelatedContentCard from "../../components/RelatedContentCard/RelatedContentCard";
import ScriptBoxCaller from "../../components/ScriptBoxCaller/ScriptBoxCaller";
import TelevisionIcon from "../../components/VectorIcons/TelevisionIcon";
import SimpleButton from "../../components/SimpleButton/SimpleButton";
import CharactersTable from "./CharactersTable/CharactersTable";
import SeasonsCarousel from "./SeasonsCarousel/SeasonsCarousel";
import LoadError from "../../components/LoadError/LoadError";
import ActivitiesCard from "../../components/ActivitiesCard/ActivitiesCard";
import PhasesCard from "../../components/PhasesCard/PhasesCard";
import RecommendationCard from "./RecommendationCard/RecommendationCard";
import ContentAttachments from "./ContentAttachments/ContentAttachments";
import ContentHeader from "./ContentHeader/ContentHeader";
import LightBoxes from "./LightBoxes/LightBoxes";
import { sendEvent } from "../../services/GoogleTagManager/methods";
import { handleSchedulerTypes } from "../../shared/utils/handleSchedulerTypes";
import { handleDramaturgyTypeByFormat } from "../../shared/utils/handleDramaturgyTypeByFormat";
import TagStatus from "../../components/TagStatus/TagStatus";
import { getService, putService } from "../../services/axios/services";
import InfoPaperExternalProject from "./InfoPaper/InfoPaperExternalProject/InfoPaperExternalProject";
import ListVideos from "./ListVideos/ListVideos";
import InfoCards from "./InfoCards/InfoCards";
import Financing from "./Financing/Financing";
import ProducerInfo from "./ProducerInfo/ProducerInfo";
import CultureHubAssessments from "./Assessments/CultureHub/CultureHubAssessments";
import ScriptBox from "../../components/ScriptBox/ScriptBox";
import TableListVideos from "./ListVideos/TableListVideos";
import impressoraIcon from "../../assets/icons/impressora.png";
import ExternalPDF from "./Documents/ExternalPDF";
import SchedulerView from "./SchedulerView/SchedulerView";
import { RecommendationsAPIContext } from "../../context/RecommendationsAPI/recommendationsAPIContext";
import { RECOMMENDATION_API_CONSTANTS } from "../../context/RecommendationsAPI/constants";
import CustomTooltip from "../../components/CustomTooltip/CustomTooltip";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useResolvedPlot } from "../../hooks/useResolvedPlot";
import {
  createEmptyPlot,
  isPlotValid,
  normalizePlot,
} from "../../../src/shared/utils/handlePlot";
import { useSheetAttachments } from "../../hooks/useSheetAttachments";

const isValidThematics = (list) =>
  list?.thematic ? Object.values(list.thematic).some((e) => !!e.length) : false;

const isValidUniverses = (list) =>
  list?.universe ? Object.values(list.universe).some((e) => !!e.length) : false;

const validDramaturgiesComplementary = (list) => {
  return (
    !!list?.contentOrigns?.length ||
    !!list?.narratives?.length ||
    !!list?.genres?.length ||
    !!list?.subGenres?.length ||
    !!list?.spaces?.length ||
    !!list?.locations?.length ||
    !!list?.periods?.length ||
    !!list?.epochs?.length ||
    !!list?.storyLine
  );
};

const validVarietiesComplementary = (list) => {
  return (
    !!list?.genres?.length ||
    !!list?.subGenres?.length ||
    !!list?.tags?.length ||
    !!list?.locations?.length ||
    !!list?.logLine
  );
};

const validLiteraryComplementary = (list) => {
  return (
    !!list?.genres?.length || !!list?.universe?.length || !!list?.storyLine
  );
};

const ProjectSheet = () => {
  const routeParams = useParams();
  const appContext = useContext(AppContext);
  const sheetContext = useContext(ProjectSheetContext);
  const { hasPermission } = useContext(UserContext);
  const {
    getWorkflowStatusByContentId,
    abortWorkflowStatusConnection,
    setShowProcessStatusCard,
    processData,
    workflowOutput,
    workflowNoContent,
    fetchAndSetWorkflowOutput,
    handleDiscardWorkflow,
    resetWorkflowState,
  } = useContext(RecommendationsAPIContext);

  const [stateExternalProject, setStateExternalProject] = useState({});
  const [stateCurrentTab, setStateCurrentTab] = useState("projectSheet");
  const [pdfOpen, setPdfOpen] = useState(false);
  const [owner, setOwner] = useState();
  const [sharingPermissions, setSharingPermissions] = useState(null);
  const { attachments, loadingAttachments, refetchAttachments } =
    useSheetAttachments({
      contentId: sheetContext.list.result?.id,
    });

  const close = () => setPdfOpen(!pdfOpen);

  let compareTypes = {
    dramaturgia: {
      endPoint: "dramaturgies",
      permission: "Dramaturgies",
      isValidComplementary: validDramaturgiesComplementary,
      chaptersOrEpisodes: "Capítulo/Episódio",
      activityEvaluation: "dramaturgia",
    },
    variedades: {
      endPoint: "varieties",
      permission: "Varieties",
      isValidComplementary: validVarietiesComplementary,
      chaptersOrEpisodes: "Episódio",
      activityEvaluation: "variedades",
    },
    "obra-literaria": {
      endPoint: "literaryworks",
      permission: "LiteraryWorks",
      isValidComplementary: validLiteraryComplementary,
      chaptersOrEpisodes: "",
      activityEvaluation: "obra-literaria",
    },
  };

  useEffect(() => {
    let body = document.querySelector("body");
    body.scrollTo(0, 0);
    sheetContext._getProjectSheet(
      routeParams.id,
      compareTypes[routeParams.type].endPoint
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParams.id]);

  useEffect(() => {
    if (
      routeParams.type === "dramaturgia" &&
      sheetContext.list?.result?.id
    ) {
      sheetContext._getPlotContent(sheetContext.list.result.id);
    }
  }, [sheetContext.list?.result?.id]);

  useEffect(() => {
    appContext.getTypeThesaurus(
      "1",
      compareTypes[routeParams.type].endPoint
    );
    appContext.getTypeThesaurus(
      "21",
      compareTypes[routeParams.type].endPoint
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const compileChannels = () => {
    const compiledChannels = [];
    const channels = sheetContext.list.result.channel;

    if (!channels?.length) return [];

    channels.forEach((channel) => {
      if (!channel.content.parentId) {
        compiledChannels.push({
          parentName: channel.content.name,
          parentId: channel.content.parentId,
          description: channel.descriptions[0],
          children: channels.filter(
            (item) => item.content.parentId === channel.content.id
          ),
        });
      }
    });
    return compiledChannels;
  };

  const list = sheetContext.list.result;
  const rawPlot = sheetContext.plot.result;

  const safeApiPlot = useMemo(() => {
    if (!rawPlot) return createEmptyPlot();

    if (Array.isArray(rawPlot)) {
      return normalizePlot({
        source: "API",
        items: rawPlot,
      });
    }

    return rawPlot;
  }, [rawPlot]);

  const { resolvedPlot, isAIActive } = useResolvedPlot({
    apiPlot: rawPlot,
    aiWorkflow: workflowOutput,
    workflowNoContent,
  });

  const isExternal = !!list?.projectStatus && !!list?.creationMode;

  const schedulerType =
    list?.projectType === "Dramaturgia"
      ? handleSchedulerTypes(handleDramaturgyTypeByFormat(list?.format?.name))
      : handleSchedulerTypes(list?.projectType);

  const getSharedAttachmentsPermissionsByProjectID = useCallback(() => {
    getService({
      endpoint: `Sharing/attachments/projects/${routeParams.id}/permissions`,
    }).then((response) => {
      setSharingPermissions(response);
    });
  }, [routeParams.id]);

  useEffect(() => {
    if (!!list?.productionCompanies?.[0]?.id && isExternal) {
      (async () => {
        const data = await getService({
          endpoint: `productioncompanies/${list?.productionCompanies?.[0]?.id}/owner`,
        });
        setOwner(data?.data);
      })();
    }
  }, [list, isExternal]);

  useEffect(() => {
    getSharedAttachmentsPermissionsByProjectID();
  }, [getSharedAttachmentsPermissionsByProjectID]);

  const { enabled: AI_PLOT_RECOMMENDATION_FEATURE_IS_ENABLED } = useFeatureFlag(
    "AI_PLOT_RECOMMENDATION"
  );

  useEffect(() => {
    if (
      list?.id &&
      AI_PLOT_RECOMMENDATION_FEATURE_IS_ENABLED &&
      hasPermission({ required: ["Dramaturgies"] })
    ) {
      getWorkflowStatusByContentId(list.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list?.id]);

  useEffect(() => {
    return () => {
      setShowProcessStatusCard(false);
      resetWorkflowState();
      abortWorkflowStatusConnection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list?.id]);

  useEffect(() => {
    if (
      processData?.status ===
        RECOMMENDATION_API_CONSTANTS.TASK_STATUS_COMPLETE &&
      processData?.correlationId &&
      !workflowOutput &&
      list?.id
    ) {
      fetchAndSetWorkflowOutput({
        correlationId: processData.correlationId,
        taskName: processData?.taskData?.Name,
      });
    }
  }, [
    processData?.status,
    processData?.correlationId,
    workflowOutput,
    fetchAndSetWorkflowOutput,
  ]);

  if (sheetContext.list.loading) {
    return <TableLoading />;
  }

  if (list === undefined) return <LoadError />;

  const compileDataId = (data) => {
    if (!data?.length) return [];

    let i = [];

    if (!!data) {
      data.forEach((el) => i.push(el.id));
    }
    return i;
  };

  const returnDays = (days) => (days + 1 > 0 ? `${days + 1} dias` : "-");

  const compileContentOriginIds = () => {
    let content = [];

    if (list.contentOrigns.length > 0) {
      list.contentOrigns.forEach((el) => {
        content.push({
          contentOriginId: el.content.id,
          contentOriginDescriptions: el.descriptions,
        });
      });
    }
    return content;
  };

  let dataProject = {};
  let basicDataDramaturgiesAndVarieties = {};

  if (
    compareTypes[routeParams.type].endPoint === "dramaturgies" ||
    compareTypes[routeParams.type].endPoint === "varieties"
  ) {
    basicDataDramaturgiesAndVarieties = {
      id: routeParams.id,
      receivedAt: list.receivedAt,
      creators: sheetContext.compileCreators(list.creators),
      notCompileCreators: list.creators,
      chaptersOrEpisodes: list.chaptersOrEpisodes,
      formatId: list.format.id,
      origins: list.origins,
      channel: list.channel,
      seasonNumber: list.seasonNumber,
      notCompileProductionCompanies: list.productionCompanies,
      productionCompanies: sheetContext.compileProduct(
        list.productionCompanies
      ),
      title: list.title,
    };
  }

  if (compareTypes[routeParams.type].endPoint === "dramaturgies") {
    dataProject = {
      basic: {
        ...basicDataDramaturgiesAndVarieties,
        soapOperaSchedule: !!list?.soapOperaSchedule
          ? list?.soapOperaSchedule?.id
          : null,
      },
      complementaries: {
        genresIds: compileDataId(list.genres),
        subGenresIds: compileDataId(list?.subGenres),
        spacesIds: compileDataId(list?.spaces),
        locationsIds: !!list?.locations?.length
          ? list?.locations?.map(({ locationId }) => locationId)
          : [],
        contentOrigns: !!list?.contentOrigns ? compileContentOriginIds() : null,
        narratives: compileDataId(list?.narratives),
        periods: compileDataId(list?.periods),
        epochIds: compileDataId(list?.epochs),
        storyLine: list?.storyLine,
      },
      relevance: {
        plot: {
          primariesIds: compileDataId(list?.plot?.primaries),
          secundariesIds: compileDataId(list?.plot?.secundaries),
          tertiariesIds: compileDataId(list?.plot?.tertiaries),
        },
        thematic: {
          primariesIds: compileDataId(list?.thematic?.primaries),
          secundariesIds: compileDataId(list?.thematic?.secundaries),
          tertiariesIds: compileDataId(list?.thematic?.tertiaries),
        },
        universe: {
          primariesIds: compileDataId(list?.universe?.primaries),
          secundariesIds: compileDataId(list?.universe?.secundaries),
          tertiariesIds: compileDataId(list?.universe?.tertiaries),
        },
      },
    };
  }

  if (compareTypes[routeParams.type].endPoint === "varieties") {
    dataProject = {
      basic: {
        ...basicDataDramaturgiesAndVarieties,
        programFrequency: list?.programFrequency?.id,
      },
      complementaries: {
        genresIds: compileDataId(list?.genres),
        subGenresIds: compileDataId(list?.subGenres),
        logLine: list?.logLine,
        tags: list?.tags,
        locationsIds: !!list?.locations?.length
          ? list?.locations?.map(({ locationId }) => locationId)
          : [],
      },
    };
  }

  if (compareTypes[routeParams.type].endPoint === "literaryworks") {
    dataProject = {
      basic: {
        id: routeParams.id,
        authors: list?.authors?.map((e) => e.value || e.id),
        publishers: list?.publishers?.map((e) => e.value || e.id),
        publicDomain: list?.publicDomain || false,
        receivedAt: list?.receivedAt,
        suggestedBy:
          list?.suggestedBy?.map((item) => ({
            ...item,
            label: item.name,
            suggesterId: item.id,
            value: item.id,
          })) || [],
        title: list?.title || "",
        formatId: list?.format?.id || "",
        publicationYear: list?.publicationYear || "",
      },
      complementaries: {
        genresIds: compileDataId(list?.genres),
        universeIds: compileDataId(list?.universe),
        storyLine: list?.storyLine,
      },
    };
  }

  const getStatusExternalProject = (data) => setStateExternalProject(data);

  const isValidCost = () =>
    list?.costAndFinancialInformation?.publicNoteCost > 0 &&
    list?.costAndFinancialInformation?.ownResourcesCost > 0 &&
    list?.costAndFinancialInformation?.nationalCoProducerCost > 0 &&
    list?.costAndFinancialInformation?.internationalCoProducerCost > 0 &&
    list?.costAndFinancialInformation?.costPerEpisode > 0 &&
    list?.costAndFinancialInformation?.totalCost > 0;

  const isYoutube = (url) => {
    if (!!url) {
      let p =
        /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
      return p.test(url);
    }
  };

  const handleChangeStatusExternalProject = () => {
    putService({
      endpoint: `externalproject/status`,
      putData: stateExternalProject,
    })
      .then((res) => {
        toastSuccess("Projeto alterado com sucesso!");
        sheetContext._getProjectSheet(
          routeParams.id,
          compareTypes[routeParams.type].endPoint
        );
        appContext.setCurrentStepBox("closed");
        sendEvent("Projeto externo", "Alterou status");
      })
      .catch((e) => {
        toastError("Erro ao alterar o projeto");
        appContext.setCurrentStepBox("closed");
      });
  };

  const evaluationDeadline = returnDays(
    calculateDifferenceInDays(list?.channels?.[0]?.deadline)
  );

  return (
    <div id="Project">
      <ContentHeader
        list={list}
        isExternal={isExternal}
        stateCurrentTab={stateCurrentTab}
        setStateCurrentTab={setStateCurrentTab}
        schedulerType={schedulerType}
        hasPermission={hasPermission}
      />

      <SchedulerView
        contentId={list.id}
        projectType={list.projectType}
        contentFormat={list.format.name}
        isExternal={isExternal}
        schedulerType={schedulerType}
        stateCurrentTab={stateCurrentTab}
        reload={() => {
          getSharedAttachmentsPermissionsByProjectID();
        }}
        sharingPermissions={sharingPermissions}
      />

      <div
        style={{
          display: stateCurrentTab === "producerInfo" ? "unset" : "none",
        }}
      >
        <ProducerInfo list={list} owner={owner} />
      </div>

      <div
        style={{
          display:
            stateCurrentTab === "cultureHubAssessments" ? "unset" : "none",
        }}
      >
        <CultureHubAssessments contentId={list.id} />
      </div>

      <div
        style={{
          display: stateCurrentTab === "projectSheet" ? "unset" : "none",
        }}
      >
        <div className="mainContainer">
          <div className="infoContainer">
            {isExternal && (
              <>
                <div className="infoPaper infoPaperStatus">
                  <div className="paper">
                    <div className="paperTitle">
                      <span>Status do projeto</span>
                      <TagStatus
                        id={list.projectStatus.id}
                        name={list.projectStatus.description}
                      />
                      {list.projectStatus.id === 2 && (
                        <ScriptBoxCaller
                          callerIcon={<EditIcon />}
                          event={() =>
                            appContext.setCurrentStepBox("editExternalProject")
                          }
                        />
                      )}
                    </div>
                  </div>
                  {list.projectStatus.id === 2 && (
                    <div className="infoField">
                      <span className="infoName">PRAZO PARA AVALIAÇÃO</span>
                      <span className="children">{evaluationDeadline}</span>
                    </div>
                  )}
                  {!!list.channels?.[0]?.justification &&
                    list.projectStatus.id === 4 && (
                      <div className="infoField">
                        <span className="infoName">Justificativa</span>
                        <span className="children">
                          {list.channels?.[0]?.justification}
                        </span>
                      </div>
                    )}
                </div>
                <InfoPaperExternalProject list={list} />
              </>
            )}
            {!isExternal && (
              <>
                <InfoPaperBasic
                  paperTitle="Dados Básicos"
                  data={list}
                  contentOrigns={list.contentOrigns}
                  callerIcon={<EditIcon />}
                  callerEvent={() =>
                    appContext.setCurrentStepBox("editBasicData")
                  }
                  havePermission={hasPermission({
                    required: [
                      compareTypes[routeParams.type].permission,
                    ],
                  })}
                  receivedAt={appContext.formatDate(
                    appContext.convertDatePtBr(list.receivedAt)
                  )}
                  channels={
                    routeParams.type !== "obra-literaria" &&
                    compileChannels()
                  }
                />

                <InfoPaperComplementary
                  paperTitle="Dados Complementares"
                  data={list}
                  callerIcon={<EditIcon />}
                  contentOrigns={list.contentOrigns}
                  type={compareTypes[routeParams.type]}
                  callerEvent={() =>
                    appContext.setCurrentStepBox("editComplementaryData")
                  }
                  havePermission={hasPermission({
                    required: [
                      compareTypes[routeParams.type].permission,
                    ],
                  })}
                  isValid={compareTypes[
                    routeParams.type
                  ].isValidComplementary(list)}
                />

                {compareTypes[routeParams.type].endPoint ===
                  "dramaturgies" && (
                  <InfoPaperRelevance
                    callerEvent={(type) =>
                      appContext.setCurrentStepBox(
                        `editRelevanceComplementaryData-${type}`
                      )
                    }
                    callerIcon={(type) =>
                      ["plot"]?.includes(type) &&
                      processData?.status ===
                        RECOMMENDATION_API_CONSTANTS.TASK_STATUS_PENDING ? (
                        <CustomTooltip
                          tipLabel={
                            <EditIcon
                              color={
                                processData?.status ===
                                RECOMMENDATION_API_CONSTANTS.TASK_STATUS_PENDING
                                  ? "#CCCCCC"
                                  : "#0098CD"
                              }
                            />
                          }
                          dataTip="Processamento com IA em andamento. Aguarde a conclusão para editar."
                          position="right"
                          className="thesaurusTooltip"
                          theme="light"
                        />
                      ) : (
                        <EditIcon />
                      )
                    }
                    isDisabled={
                      processData?.status ===
                      RECOMMENDATION_API_CONSTANTS.TASK_STATUS_PENDING
                    }
                    havePermission={hasPermission({
                      required: [
                        compareTypes[routeParams.type].permission,
                      ],
                    })}
                    paperTitle="Dados Complementares por Relevância"
                    isValid={
                      isPlotValid(safeApiPlot) ||
                      isValidThematics(list) ||
                      isValidUniverses(list)
                    }
                    plots={safeApiPlot}
                    thematics={list.thematic}
                    universes={list.universe}
                    isPlotLoading={sheetContext.plot.loading}
                  />
                )}
              </>
            )}
          </div>

          <div className="cardsContainer">
            {isExternal && (
              <InfoCards
                title="Gerar Relatório em PDF"
                pdf
                callerIcon={
                  <div className="impressoraIcon">
                    <img src={impressoraIcon} alt="Abrir PDF" />
                  </div>
                }
                callerEvent={() => setPdfOpen(true)}
              />
            )}

            <InfoCard
              cardTitle="Anexos"
              callerEvent={[
                () => appContext.setCurrentStepBox("uploadAttachment"),
                () => appContext.setCurrentStepBox("editAttachment"),
              ]}
              callerIcon={
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontFamily: "Globotipo Text" }}>
                      Fazer upload de anexos
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <Box component="span">
                    <CloudIcon />
                  </Box>
                </Tooltip>
              }
              files={attachments?.items?.filter(
                (file) =>
                  !!file.fileId && file?.type?.name !== "Capítulo/Episódio"
              )}
              havePermission={hasPermission({
                required: [compareTypes[routeParams.type].permission],
              })}
              condition={
                compareTypes[routeParams.type].chaptersOrEpisodes
              }
              isExternal={isExternal}
              isMaterial={schedulerType === "Materiais"}
              projectId={sheetContext.list?.result?.id}
              loadingAttachments={loadingAttachments}
            />

            <ContentAttachments
              data={attachments?.items}
              idProject={routeParams.id}
              type={compareTypes[routeParams.type].endPoint}
              refetchAttachments={refetchAttachments}
              projectType={list.projectType}
              format={list.format?.name}
              condition={
                compareTypes[routeParams.type].chaptersOrEpisodes
              }
              reload={() => {
                sheetContext._getProjectSheet(
                  routeParams.id,
                  compareTypes[routeParams.type].endPoint
                );
                getSharedAttachmentsPermissionsByProjectID();
                refetchAttachments();
              }}
              havePermission={hasPermission({
                required: [compareTypes[routeParams.type].permission],
              })}
              isExternal={isExternal}
              isMaterial={schedulerType === "Materiais"}
              downloadContext="ficha-projeto"
              sharingPermissions={sharingPermissions}
            />

            <ActivitiesCard
              cardTitle={!isExternal ? "Atividades concluídas" : "Atividades"}
              contentType={
                compareTypes[routeParams.type].activityEvaluation
              }
              format={list?.format?.name}
              projectId={routeParams.id}
            />

            {isExternal && (
              <>
                <InfoCards
                  handleEvent={() => appContext.setCurrentStepBox("viewVideos")}
                  countData={list.videos.length}
                  hasBaseboard={true}
                  viewAll="Ver todos os vídeos"
                  title="Videos"
                >
                  {!!list.videos.length &&
                    list.videos.slice(0, 3).map((video, i) => {
                      return (
                        <React.Fragment key={i}>
                          <ListVideos
                            url={video.url}
                            key={i}
                            title={video.title}
                            password={video.password}
                            subTitle={video?.type?.description}
                            platform={
                              !!video.url && !!isYoutube(video.url)
                                ? "Youtube"
                                : "Vimeo"
                            }
                          />
                        </React.Fragment>
                      );
                    })}

                  {!list.videos.length && (
                    <div className="cardContent">
                      <span>Não há nenhum video.</span>
                    </div>
                  )}
                </InfoCards>
                <InfoCards title="Custos e Financiamentos">
                  <div className="contentCardsFinancing">
                    {isValidCost() && (
                      <React.Fragment>
                        {list?.costAndFinancialInformation?.publicNoteCost >
                          0 && (
                          <Financing
                            label="EDITAIS PÚBLICOS"
                            value={
                              list?.costAndFinancialInformation?.publicNoteCost
                            }
                          />
                        )}

                        {list?.costAndFinancialInformation?.ownResourcesCost >
                          0 && (
                          <Financing
                            label="RECURSOS PRÓPRIOS"
                            value={
                              list?.costAndFinancialInformation
                                ?.ownResourcesCost
                            }
                          />
                        )}

                        {list?.costAndFinancialInformation
                          ?.nationalCoProducerCost > 0 && (
                          <Financing
                            label="CONDUTORES NACIONAIS"
                            value={
                              list?.costAndFinancialInformation
                                ?.nationalCoProducerCost
                            }
                          />
                        )}

                        {list?.costAndFinancialInformation
                          ?.internationalCoProducerCost > 0 && (
                          <Financing
                            label="CONDUTORES INTERNACIONAIS"
                            value={
                              list?.costAndFinancialInformation
                                ?.internationalCoProducerCost
                            }
                          />
                        )}

                        {list?.costAndFinancialInformation
                          ?.cultureIncentiveLawCost > 0 && (
                          <Financing
                            label="LEIS DE INCENTIVO A CULTURA"
                            value={
                              list?.costAndFinancialInformation
                                ?.cultureIncentiveLawCost
                            }
                          />
                        )}

                        {!!list?.costAndFinancialInformation?.others?.key &&
                          !!list?.costAndFinancialInformation?.others
                            ?.value && (
                            <Financing
                              label={
                                list?.costAndFinancialInformation?.others?.key
                              }
                              value={
                                list?.costAndFinancialInformation?.others?.value
                              }
                            />
                          )}

                        <div className="borderTop" />
                      </React.Fragment>
                    )}

                    {list?.costAndFinancialInformation?.costPerEpisode > 0 && (
                      <Financing
                        label="PROPOSTAS DE ESTIMATIVA DE CUSTO POR EPISÓDIO"
                        value={
                          list?.costAndFinancialInformation?.costPerEpisode
                        }
                      />
                    )}

                    {list?.costAndFinancialInformation?.totalCost > 0 && (
                      <Financing
                        label="PROPOSTAS DE ESTIMATIVA DE CUSTO TOTAL"
                        value={list?.costAndFinancialInformation?.totalCost}
                      />
                    )}
                  </div>
                </InfoCards>
              </>
            )}

            {compareTypes[routeParams.type].endPoint !==
              "literaryworks" &&
              !isExternal && (
                <PhasesCard
                  cardTitle="Fases do projeto"
                  projectId={routeParams.id}
                  type={compareTypes[routeParams.type].endPoint}
                />
              )}
            {!isExternal && (
              <RecommendationCard
                cardTitle={`Recomendação ${
                  compareTypes[routeParams.type].endPoint ===
                  "literaryworks"
                    ? "da Obra"
                    : "do projeto"
                }`}
                callerEvent={() =>
                  appContext.setCurrentStepBox("recomendation")
                }
                recommendation={sheetContext.list.result?.recommendation}
              />
            )}
          </div>
        </div>

        {compareTypes[routeParams.type].endPoint !== "literaryworks" &&
          !isExternal && (
            <div className="relatedContentContainer">
              <div className="contentHeader">
                <span className="contentTitle">Conteúdo relacionado</span>

                {hasPermission({
                  required: [compareTypes[routeParams.type].permission],
                }) && (
                  <ScriptBoxCaller
                    callerIcon={<EditIcon />}
                    event={() =>
                      appContext.setCurrentStepBox("editRelatedContent")
                    }
                  />
                )}
              </div>

              <div className="contentCardsContainer">
                {list.associations.map((association, index) => {
                  return (
                    <RelatedContentCard
                      key={index}
                      id={association.contentId}
                      contentType={association.contentType}
                      cardTitle={
                        !!association.type ? association.type.name : ""
                      }
                      productName={association.title}
                      format={association.format}
                      soapOperaSchedule={association.soapOperaSchedule}
                      editable={association.editable}
                    />
                  );
                })}
              </div>
            </div>
          )}

        {compareTypes[routeParams.type].endPoint === "dramaturgies" &&
          !isExternal && (
            <div className="charactersContainer">
              <div className="charactersHeader">
                <span className="contentTitle">Personagens</span>

                {hasPermission({
                  required: [compareTypes[routeParams.type].permission],
                }) && (
                  <SimpleButton
                    text="Novo Personagem"
                    classButton="buttonBlue small"
                    dataQA="button-prospect-edit"
                    onBreadcrumb={true}
                  >
                    <ScriptBoxCaller
                      callerIcon={<TelevisionIcon color="#FFF" />}
                      event={() =>
                        appContext.setCurrentStepBox("createCharacter")
                      }
                    />
                  </SimpleButton>
                )}
              </div>

              <div className="charactersListContainer">
                <div className="charactersList">
                  <CharactersTable
                    id={routeParams.id}
                    havePermission={hasPermission({
                      required: [
                        compareTypes[routeParams.type].permission,
                      ],
                    })}
                  />
                </div>
              </div>
            </div>
          )}

        <SeasonsCarousel
          id={list.id}
          productId={list.productId}
          title={list.title}
          type={routeParams.type}
        />

        {appContext.current.step === "viewVideos" && (
          <ScriptBox
            open={appContext.current.step === "viewVideos"}
            removePadding={true}
            title="Videos"
            size="medium"
          >
            <TableListVideos videos={list.videos} />
          </ScriptBox>
        )}
        {isExternal && (
          <ExternalPDF
            open={pdfOpen}
            close={close}
            data={list}
            returnDays={returnDays}
            owner={owner}
          />
        )}
        <LightBoxes
          id={routeParams.id}
          type={compareTypes[routeParams.type]}
          data={{
            ...list,
            plot: workflowNoContent ? null : resolvedPlot,
          }}
          isAIPlotActive={isAIActive}
          dataProject={dataProject}
          evaluationDeadline={evaluationDeadline}
          getStatusExternalProject={getStatusExternalProject}
          handleChangeStatusExternalProject={handleChangeStatusExternalProject}
          dataStatusProject={list?.projectStatus}
          onDiscardAI={handleDiscardWorkflow}
          workflowNoContent={workflowNoContent}
        />
      </div>
    </div>
  );
};

export default ProjectSheet;
